import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

const initialState = {
  items: JSON.parse(localStorage.getItem("cartItems")) || [],
  loading: false,
  error: null,
  cartId: null,
  totalAmount: JSON.parse(localStorage.getItem("cartTotalAmount")) || 0,
  discountedAmount:
    JSON.parse(localStorage.getItem("cartDiscountedAmount")) || 0,
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    setLoading(state) {
      state.loading = true;
    },
    setError(state, action) {
      state.loading = false;
      state.error = action.payload;
    },
    addToCartSuccess(state, action) {
      const product = action?.payload?.products || action.payload;
      if (!product) return;

      const selectedWeightObj = product.weights.find(w => w._id === product.selectedWeight._id);
      if (!selectedWeightObj) return;

      const existingProduct = state.items.find(
        (item) => item.product_id === product._id && 
                  (item.weightId?._id === selectedWeightObj._id || 
                   item.weights?.find(w => w._id === selectedWeightObj._id))
      );

      if (existingProduct) {
        existingProduct.quantity += 1;
        const basePrice = selectedWeightObj.prices.India;
        existingProduct.price = basePrice;
        existingProduct.totalPrice = basePrice * existingProduct.quantity;
        existingProduct.discountedPrice = basePrice * (1 - (product.discount_percentage || 0) / 100);
        existingProduct.discountedTotalPrice = existingProduct.discountedPrice * existingProduct.quantity;
      } else {
        // Format for guest users
        state.items.push({
          product_id: product._id,
          productId: product,
          name: product.productName,
          quantity: 1,
          image: product.productImages?.[0] || "",
          discount_percentage: product.discount_percentage,
          productDescription: product.productDescription,
          productType: product.productType,
          category: product.category,
          weights: product.weights,
          weightId: selectedWeightObj,
          isAvailable: product.isAvailable,
          price: selectedWeightObj.prices.India,
          discountedPrice: selectedWeightObj.prices.India * (1 - (product.discount_percentage || 0) / 100),
          totalPrice: selectedWeightObj.prices.India,
          discountedTotalPrice: selectedWeightObj.prices.India * (1 - (product.discount_percentage || 0) / 100)
        });
      }

      // Calculate totals
      state.totalAmount = state.items.reduce((total, item) => total + item.totalPrice, 0);
      state.discountedAmount = state.items.reduce((total, item) => total + item.discountedTotalPrice, 0);

      // Save to localStorage for guest users
      if (!localStorage.getItem("token")) {
        localStorage.setItem("cartItems", JSON.stringify(state.items));
        localStorage.setItem("cartTotalAmount", JSON.stringify(state.totalAmount));
        localStorage.setItem("cartDiscountedAmount", JSON.stringify(state.discountedAmount));
      }
    },
    setCartItems(state, action) {
      if (!action.payload) {
        state.items = [];
        state.cartId = null;
        state.totalAmount = 0;
        state.discountedAmount = 0;
      } else {
        // For logged-in users, use server cart
        if (localStorage.getItem("token")) {
          // console.log("action.payload in setCartItems", action.payload);
          state.items = action.payload.items;
          state.cartId = action.payload._id;
          state.totalAmount = action.payload.totalAmount;
          state.discountedAmount = action.payload.discountedAmount;
        } else {
          // For non-logged in users, use local cart
          state.items = action.payload;
          state.cartId = null;
        }
      }
      state.loading = false;
      state.error = null;
    },
    updateCartQuantity(state, action) {
      const { productId, quantity, weightId } = action.payload;

      const item = state.items.find(
        (item) => item.product_id === productId && item.weightId._id === weightId
      );

      if (item) {
        item.quantity = quantity;
        item.totalPrice = item.weightId.prices.India * quantity;
        item.discountedPrice = item.weightId.prices.India * (1 - (item.discount_percentage || 0) / 100);
        item.discountedTotalPrice = item.discountedPrice * quantity;

        // Update total amounts
        state.totalAmount = state.items.reduce(
          (total, item) => total + item.totalPrice,
          0
        );
        state.discountedAmount = state.items.reduce(
          (total, item) => total + item.discountedTotalPrice,
          0
        );

        // Update localStorage for guest users
        if (!localStorage.getItem("token")) {
          localStorage.setItem("cartItems", JSON.stringify(state.items));
          localStorage.setItem("cartTotalAmount", JSON.stringify(state.totalAmount));
          localStorage.setItem("cartDiscountedAmount", JSON.stringify(state.discountedAmount));
        }
      }
    },
    removeFromCart(state, action) {
      const { productId, weightId } = action.payload;
      
      state.items = state.items.filter(
        (item) => !(item.product_id === productId && item.weightId._id === weightId)
      );

      // Update total amounts after removal
      state.totalAmount = state.items.reduce(
        (total, item) => total + item.totalPrice,
        0
      );
      state.discountedAmount = state.items.reduce(
        (total, item) => total + item.discountedTotalPrice,
        0
      );

      // Update localStorage for guest users
      if (!localStorage.getItem("token")) {
        localStorage.setItem("cartItems", JSON.stringify(state.items));
        localStorage.setItem("cartTotalAmount", JSON.stringify(state.totalAmount));
        localStorage.setItem("cartDiscountedAmount", JSON.stringify(state.discountedAmount));
      }
    },
    clearCart(state) {
      state.items = [];
      state.cartId = null;
      state.totalAmount = 0;
      state.discountedAmount = 0;
      localStorage.removeItem("cartItems");
      localStorage.removeItem("cartTotalAmount");
      localStorage.removeItem("cartDiscountedAmount");
    },
  },
});

export const {
  setLoading,
  setError,
  addToCartSuccess,
  setCartItems,
  updateCartQuantity,
  removeFromCart,
  clearCart,
} = cartSlice.actions;

// Thunk for adding to cart
export const addToCartAPI =
  (product, selectedWeight) => async (dispatch, getState) => {
    // console.log("product in addToCartAPI", product);
    // console.log("selectedWeight in addToCartAPI", selectedWeight);
    if (!product) {
      toast.error("Invalid product data");
      return;
    }

    const { auth } = getState();
    const { token, user } = auth;

    // Handle non-logged in users
    if (!token) {
      dispatch(addToCartSuccess(product, selectedWeight));
      toast.success("Product added to cart");
      return;
    }

    // Handle logged in users
    try {
      dispatch(setLoading());

      if (!user?.id) {
        throw new Error("User data not found");
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/carts/add`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: user.id,
            productId: product._id,
            quantity: 1,
            weightId: product.weights.find(
              (w) => w.weightInGrams === selectedWeight
            )._id,
          }),
        }
      );
      const data = await response.json();
      const { success, message, responseObject } = data;
      if (success) {
        dispatch(setCartItems(responseObject));
        toast.success(message);
      } else {
        throw new Error(message);
      }
    } catch (error) {
      console.error("Add to cart error:", error);
      toast.error(error.message || "Failed to add to cart");
      dispatch(setError(error.message));
    }
  };

// Async thunk for fetching cart data
export const fetchCartProducts = () => async (dispatch, getState) => {
  const { auth } = getState();
  const { user, token } = auth;

  if (!user || !token) {
    // console.log("User or token not found");
    // For non-logged-in users, get cart from localStorage
    const localCart = JSON.parse(localStorage.getItem("cartItems")) || [];
    dispatch(setCartItems(localCart));
    return;
  }

  try {
    // console.log("User or token found");
    dispatch(setLoading());
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/carts/${user.id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await response.json();
    const { success, message, responseObject } = data;
    if (success) {
      dispatch(setCartItems(responseObject));
    } else {
      dispatch(setError(message));
      dispatch(setCartItems(null));
    }
  } catch (error) {
    toast.error("Error fetching cart data");
    dispatch(setError(error.message));
    dispatch(setCartItems(null));
  }
};

// API action to update cart item quantity
export const updateCartQuantityAPI =
  (productId, quantity, weight) => async (dispatch, getState) => {
    const { auth } = getState();
    const { token, user } = auth;
    // // console.log("productId in updateCartQuantityAPI", productId);
    // // console.log("quantity in updateCartQuantityAPI", quantity);
    // // console.log("weight in updateCartQuantityAPI", weight);
    // For non-logged in users
    if (!token || !user?.id) {
      // // console.log("weight in updateCartQuantityAPI", weight)
      dispatch(updateCartQuantity({ productId, quantity, weightId: weight }));
      return;
    }

    try {
      dispatch(setLoading());

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/carts/update-quantity`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: user.id,
            productId,
            quantity,
            weightId: weight,
          }),
        }
      );

      const data = await response.json();
      const { success, message, responseObject } = data;

      if (success) {
        dispatch(setCartItems(responseObject));
        // No need to dispatch updateCartQuantity here as setCartItems will handle it
      } else {
        throw new Error(message);
      }
    } catch (error) {
      console.error("Update quantity error:", error);
      toast.error("Failed to update quantity.");
      dispatch(setError(error.message));
    }
  };

// API action to update cart item quantity
export const deleteCartItemApi =
  ({ productId, weightId }) =>
  async (dispatch, getState) => {
    const { auth } = getState();
    const { token, user } = auth;

    // // console.log("weights in deleteCartItemApi",weights)
    // console.log("productId in deleteCartItemApi", productId);
    // console.log("weightId in deleteCartItemApi", weightId);

    if (!token || !user) {
      dispatch(removeFromCart({ productId, weightId }));
      toast.success("Item removed from cart");
      return;
    }

    try {
      dispatch(setLoading());

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/carts/${user.id}/item/${productId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: user.id,
            productId,
            weightId: weightId,
          }),
        }
      );

      const data = await response.json();
      const { success, message, responseObject } = data;

      if (success) {
        dispatch(setCartItems(responseObject));
        toast.success(message);
      } else {
        throw new Error(message);
      }
    } catch (error) {
      console.error("Error deleting cart item:", error);
      toast.error(error.message || "Error deleting cart item");
      dispatch(setError(error.message));
    }
  };

// Update the handleLogout to be a thunk action
export const handleLogout = () => (dispatch) => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  // Don't remove cartItems on logout if you want to maintain cart
  dispatch(clearCart());
};

// Thunk for merging carts after login
export const mergeCartsAfterLogin = () => async (dispatch) => {
  const localCart = JSON.parse(localStorage.getItem("cartItems")) || [];
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user"));

  if (!localCart.length || !token || !user) {
    dispatch(fetchCartProducts());
    return;
  }

  try {
    // Format cart items correctly for the API
    const cartItems = localCart.map((item) => ({
      productId: item.product_id, // Changed from product to product_id
      quantity: item.quantity,
      weightId: item.weightId._id, // Changed to get the _id from weightId object
    }));

    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/carts/merge`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: user.id,
          items: cartItems,
        }),
      }
    );

    const data = await response.json();
    if (data.success) {
      dispatch(setCartItems(data.responseObject));
      localStorage.removeItem("cartItems"); // Clear local cart after successful merge
      toast.success("Cart merged successfully");
    } else {
      throw new Error(data.message);
    }
  } catch (error) {
    console.error("Error merging carts:", error);
    toast.error(error.message || "Failed to merge carts");
  }
};

export default cartSlice.reducer;
